import { useCallback, useMemo } from "react";
import { degreesToRadians, polarToCartesian } from "utils/converters";

interface UseGaugeParams {
  diameter: number;
  startAngle: number;
  endAngle: number;
  numTicks: number;
  domain: [number, number];
}

interface GetTickPropsParams {
  length: number;
  angle: number;
  offset: number;
}

interface GetLabelPropsParams {
  angle: number;
  offset: number;
}

interface GetArcPropsParams {
  offset?: number;
  startAngle: number;
  endAngle: number;
}

const makeTickMarks = (
  minAngle: number,
  maxAngle: number,
  numTicks: number
): Array<number> => {
  const tickMarks: Array<number> = [];
  const angleRange: number = maxAngle - minAngle;
  const angleStep = angleRange / numTicks;
  for (let i = 1; i < numTicks; i++) {
    tickMarks.push(minAngle + i * angleStep);
  }
  return tickMarks.reverse();
};

export const useGauge = (params: UseGaugeParams) => {
  const { startAngle, endAngle, numTicks, diameter, domain } = params;
  const radius = diameter / 2;
  const [minValue, maxValue] = domain;

  const ticks: number[] = useMemo(() => {
    return makeTickMarks(startAngle, endAngle, numTicks).reverse();
  }, [startAngle, endAngle, numTicks]);

  const getLabelProps = useCallback(
    (params: GetLabelPropsParams) => {
      const { angle, offset } = params;
      const p1 = polarToCartesian(0, 0, radius - offset, angle + 90);

      return {
        x: p1.x,
        y: p1.y,
        dominantBaseline: "middle",
        textAnchor: "middle",
      };
    },
    [diameter, radius]
  );

  const getTickProps = useCallback(
    (params: GetTickPropsParams) => {
      const { length, angle, offset } = params;
      const p1 = polarToCartesian(
        0,
        0,
        radius + offset - length / 2,
        angle + 90
      );
      const p2 = polarToCartesian(
        0,
        0,
        radius + length / 2 + offset,
        angle + 90
      );

      return {
        x1: p1.x,
        x2: p2.x,
        y1: p1.y,
        y2: p2.y,
      };
    },
    [ticks, diameter, radius]
  );

  const angleToValue = (angle: number) => {
    const angleRange = endAngle - startAngle;
    const valueRange = maxValue - minValue;
    const value = minValue + ((angle - startAngle) / angleRange) * valueRange;
    return value;
  };

  const valueToAngle = (value: number | undefined) => {
    if (value === null || value === undefined || !isFinite(value)) return startAngle;
    const angleRange = endAngle - startAngle;
    const valueRange = maxValue - minValue;
    if (valueRange === 0) return startAngle;
    // Clamp to the domain so out-of-range values can't produce angles
    // outside the arc, which would make paths sweep the wrong way around
    const clamped = Math.min(maxValue, Math.max(minValue, value));
    const angle = startAngle + ((clamped - minValue) / valueRange) * angleRange;
    return angle;
  };

  const getArcProps = useCallback(
    (params: GetArcPropsParams) => {
      const { offset = 0, startAngle, endAngle, ...rest } = params;

      const start = polarToCartesian(0, 0, radius + offset, startAngle + 90);
      const end = polarToCartesian(0, 0, radius + offset, endAngle + 90);

      const largeArcFlag = endAngle - startAngle < 180 ? "0" : "1";

      const d = [
        "M",
        start.x,
        start.y,
        "A",
        radius + offset,
        radius + offset,
        0,
        largeArcFlag,
        1,
        end.x,
        end.y,
      ].join(" ");

      return {
        d,
        ...rest,
      };
    },
    [diameter, radius]
  );

  const calculatediameterForDirection = useCallback(
    (startAngle: number, deg: number) => {
      const angle = startAngle - deg;
      const distance = (Math.cos(degreesToRadians(angle)) * diameter) / 2;
      return distance;
    },
    [diameter]
  );

  const getSVGProps = (offset = 0) => {
    const getDistanceForDirection = (deg: number) => {
      if (startAngle < deg && endAngle > deg) return diameter / 2;
      const startAngleDistance = calculatediameterForDirection(
        startAngle + 90,
        deg + 90
      );
      const endAngleDistance = calculatediameterForDirection(
        endAngle + 90,
        deg + 90
      );
      return Math.max(0, startAngleDistance, endAngleDistance);
    };

    const [top, right, bottom, left] = [
      getDistanceForDirection(180),
      getDistanceForDirection(270),
      getDistanceForDirection(0),
      getDistanceForDirection(90),
    ];

    const width = left + right;
    const height = top + bottom;

    const viewBox = [
      -left - offset,
      -top - offset,
      width + 2 * offset,
      height + 2 * offset,
    ].join(" ");

    return {
      width,
      height,
      viewBox,
    };
  };

  return {
    ticks,
    getTickProps,
    getLabelProps,
    valueToAngle,
    angleToValue,
    getArcProps,
    getSVGProps,
  };
};
